import { UseQueryOptions } from '@tanstack/react-query';
import { PaginationState } from '@tanstack/react-table';
import {
  ConnectionDto,
  DarDto,
  ParticipantStatusDto,
  RemoteParticipantConnectionControllerService,
  RemoteParticipantControllerService,
  RemoteParticipantDarControllerService,
  RemoteParticipantDto,
  type RemoteParticipantFilterResponseDto,
  RemoteParticipantPruneControllerService,
  RemoteParticipantStatusControllerService,
  ScheduledPruneDto,
} from 'data/openapi';
import { useQuery, useMutation } from 'data/utils/useQuery';
import { useDataConfig } from 'data/utils/hooks';
import { QueryKeys } from 'data/constants';

export const useRemoteParticipants = (
  options?: UseQueryOptions<RemoteParticipantFilterResponseDto>,
  pagination?: PaginationState,
  healthStatus?: boolean,
  status?: RemoteParticipantDto.phase,
  name?: string,
) => {
  let from: number | undefined;
  let limit: number | undefined;
  if (pagination) {
    const { pageSize, pageIndex } = pagination as PaginationState;
    from = pageIndex;
    limit = pageSize;
  }
  const { pollingInterval } = useDataConfig();
  return useQuery(
    [QueryKeys.REMOTE_PARTICIPANTS, from ?? null, limit ?? null, healthStatus, status, name],
    () =>
      RemoteParticipantControllerService.listRemoteParticipants(
        healthStatus,
        from && limit ? from * limit : undefined,
        status,
        limit,
        name,
      ),
    {
      refetchInterval: pollingInterval,
      keepPreviousData: true,
      ...options,
    },
  );
};

export const useRemoteParticipant = (name: string, options?: UseQueryOptions<RemoteParticipantDto>) => {
  const { pollingInterval } = useDataConfig();
  return useQuery(
    [QueryKeys.REMOTE_PARTICIPANTS, name],
    () => RemoteParticipantControllerService.getRemoteParticipant(name),
    {
      refetchInterval: pollingInterval,
      ...options,
    },
  );
};

export const useDeleteRemoteParticipant = () => {
  return useMutation(async (name: string) => {
    await RemoteParticipantControllerService.deleteRemoteParticipant(name);
  });
};

export const useCreateRemoteParticipant = () => {
  return useMutation(async (values: RemoteParticipantDto) => {
    await RemoteParticipantControllerService.createRemoteParticipant(values);
  });
};

export const useUpdateRemoteParticipant = () => {
  return useMutation(async (values: RemoteParticipantDto) => {
    await RemoteParticipantControllerService.updateRemoteParticipant(values);
  });
};

export const useCreateRemotePntConnection = (name: string) => {
  return useMutation(async ({ values }: { values: ConnectionDto }) => {
    await RemoteParticipantConnectionControllerService.createRemoteParticipantConnection(name, values);
  });
};

// TODO will be used for remote participant as well when BE ready
export const useRemoteParticipantStatus = (
  name: string,
  options?: UseQueryOptions<unknown, any, ParticipantStatusDto>,
) => {
  const { pollingInterval } = useDataConfig();
  return useQuery(
    [QueryKeys.REMOTE_PARTICIPANTS, name, 'status'],
    () => RemoteParticipantStatusControllerService.getRemoteParticipantHealthStatus(name),
    {
      refetchInterval: pollingInterval,
      ...options,
    },
  );
};

export const useRemoteParticipantDars = (name: string, options?: UseQueryOptions<unknown, any, DarDto[]>) => {
  return useQuery(
    [QueryKeys.REMOTE_PARTICIPANTS, name, 'dars'],
    () => RemoteParticipantDarControllerService.listRemoteParticipantDars(name),
    options,
  );
};

export const useUploadDarToRemoteParticipant = (name: string) => {
  return useMutation(async (darName: Array<string>) => {
    await RemoteParticipantDarControllerService.uploadRemoteParticipantDar(name, darName);
  });
};

export const useRemoveDarFromRemoteParticipant = (name: string) => {
  return useMutation(async (darHash: string) => {
    await RemoteParticipantDarControllerService.removeRemoteParticipantDar(name, darHash);
  });
};

export const useRemoteParticipantPruningSchedules = (
  name: string,
  options?: UseQueryOptions<unknown, any, ScheduledPruneDto>,
) => {
  return useQuery(
    [QueryKeys.REMOTE_PARTICIPANTS, name, QueryKeys.PRUNE, QueryKeys.REMOTE],
    () => RemoteParticipantPruneControllerService.getRemoteParticipantPruningSchedule(name),
    options,
  );
};

export const useCreateRemoteParticipantPruningSchedule = (name: string) => {
  return useMutation(async ({ values }: { values: ScheduledPruneDto }) => {
    await RemoteParticipantPruneControllerService.createRemoteParticipantPruningSchedule(name, values);
  });
};

export const useDeleteRemoteParticipantPruningSchedule = () => {
  return useMutation(async ({ name }: { name: string }) => {
    await RemoteParticipantPruneControllerService.deleteRemoteParticipantPruningSchedule(name);
  });
};
